import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  ArrowLeft, ArrowRight, Loader2, Lock, RefreshCw, RotateCw, Sparkles, ImageIcon,
  PenTool, Check, X, BookmarkCheck, Layers,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const refreshBilling = () => window.dispatchEvent(new Event("pm-billing-refresh"));

function readError(e, fallback) {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  if (status === 402) {
    return { paywall: true, message: (detail && detail.reason) || "You're out of credits." };
  }
  return { paywall: false, message: typeof detail === "string" ? detail : fallback };
}

const costLabel = (c) => (c ? `${c} credit` : "free");

function ErrorNote({ error, testid }) {
  if (!error) return null;
  return (
    <div className="pm-card p-4 mt-4 text-sm" style={{ borderColor: "var(--weakness)" }} data-testid={testid}>
      <div className="flex items-start gap-2">
        {error.paywall && <Lock size={16} className="mt-0.5 shrink-0" style={{ color: "#c98a3b" }} />}
        <div>
          <p style={{ color: "var(--text)" }}>{error.message}</p>
          {error.paywall && (
            <div className="flex gap-2 mt-3 flex-wrap">
              <a href="/app/plans" className="pm-btn-primary text-xs" data-testid={`${testid}-upgrade`}>Upgrade</a>
              <a href="/app/plans#credit-packs" className="pm-btn-secondary text-xs" data-testid={`${testid}-buy`}>Buy credits</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function GeneratePanel({ topic, cost, busy, failedMsg, error, onGenerate }) {
  return (
    <div className="pm-card p-8 text-left" data-testid="term-deck-intro">
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5" style={{ background: "var(--primary)" }}>
        <Layers size={20} color="white" />
      </div>
      <h2 className="font-display text-2xl font-bold mb-2">Build your {topic.name} set</h2>
      <ul className="text-sm space-y-2 mb-6" style={{ color: "var(--text-secondary)" }}>
        <li className="flex gap-2"><Check size={15} className="mt-0.5 shrink-0" style={{ color: "var(--mastery)" }} />Every testable term from the official AAMC outline for this topic (about 15–40 cards).</li>
        <li className="flex gap-2"><Check size={15} className="mt-0.5 shrink-0" style={{ color: "var(--mastery)" }} />Clean term → definition format, ordered foundational to advanced.</li>
        <li className="flex gap-2"><Check size={15} className="mt-0.5 shrink-0" style={{ color: "var(--mastery)" }} />Saved to your account — reopen it any time for free.</li>
      </ul>
      {failedMsg && <p className="text-sm mb-4" style={{ color: "var(--weakness)" }} data-testid="term-deck-failed">{failedMsg}</p>}
      <button onClick={onGenerate} disabled={busy} className="pm-btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="term-deck-generate-btn">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
        Generate set · {costLabel(cost)}
      </button>
      <ErrorNote error={error} testid="term-deck-generate-error" />
    </div>
  );
}

function GeneratingPanel({ topic }) {
  return (
    <div className="pm-card p-10 text-center" data-testid="term-deck-generating">
      <Loader2 size={32} className="animate-spin mx-auto mb-4" style={{ color: "var(--primary)" }} />
      <p className="font-display font-bold text-lg mb-1">Writing your {topic.name} set…</p>
      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
        Covering every AAMC subtopic. This takes about a minute — you can leave and come back, it saves automatically.
      </p>
    </div>
  );
}

function CardImage({ card, cost, busy, error, onAdd }) {
  const [redo, setRedo] = useState(false);
  const stop = (e) => e.stopPropagation();
  const choices = (
    <div className="flex flex-wrap gap-2" onClick={stop}>
      <button onClick={() => { setRedo(false); onAdd("analogy"); }} disabled={busy} className="pm-btn-secondary text-xs inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="term-card-add-analogy-img">
        <ImageIcon size={12} /> Visual analogy · {costLabel(cost)}
      </button>
      <button onClick={() => { setRedo(false); onAdd("diagram"); }} disabled={busy} className="pm-btn-secondary text-xs inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="term-card-add-diagram-img">
        <PenTool size={12} /> Labeled diagram · {costLabel(cost)}
      </button>
    </div>
  );
  return (
    <div className="mt-5" onClick={stop}>
      {busy ? (
        <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }} data-testid="term-card-image-loading">
          <Loader2 size={14} className="animate-spin" /> Drawing your image…
        </div>
      ) : card.image_url ? (
        <>
          <img src={card.image_url} alt={card.term} className="w-full rounded-xl max-h-64 object-contain bg-white" data-testid="term-card-image" />
          <div className="flex items-center gap-3 mt-2 text-xs" style={{ color: "var(--text-muted)" }}>
            <span data-testid="term-card-image-style">{card.image_style === "diagram" ? "Labeled diagram" : "Visual analogy"} · saved</span>
            <button onClick={() => setRedo(!redo)} className="hover:underline" style={{ color: "var(--primary)" }} data-testid="term-card-redo-image-btn">
              <RefreshCw size={11} className="inline mr-1" />Redo image
            </button>
          </div>
          {redo && <div className="mt-2">{choices}</div>}
        </>
      ) : (
        <>
          <div className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>Optional: add a picture to this card</div>
          {choices}
        </>
      )}
      <ErrorNote error={error} testid="term-card-image-error" />
    </div>
  );
}

function StudyCard({ card, flipped, onFlip, imageProps }) {
  return (
    <div
      onClick={onFlip}
      className="pm-card p-8 cursor-pointer select-none min-h-[380px] flex flex-col justify-center relative transition-colors"
      style={{ background: flipped ? "var(--hl-yellow)" : "var(--paper)" }}
      data-testid="term-card"
    >
      {!flipped ? (
        <div className="text-center">
          {card.aamc_focus && (
            <div className="pm-badge mb-6" style={{ background: "var(--muted)", color: "var(--text-muted)" }} data-testid="term-card-focus">{card.aamc_focus}</div>
          )}
          <div className="font-display text-3xl md:text-4xl font-bold" data-testid="term-card-term">{card.term}</div>
          <div className="text-sm mt-8" style={{ color: "var(--text-muted)" }}><RotateCw size={14} className="inline mr-1" /> Tap to see the definition</div>
        </div>
      ) : (
        <div className="text-left" data-testid="term-card-back">
          <div className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "var(--text-muted)" }}>{card.term}</div>
          <p className="font-display text-xl leading-snug" data-testid="term-card-definition">{card.definition}</p>
          <CardImage card={card} {...imageProps} />
        </div>
      )}
    </div>
  );
}

function StudyControls({ idx, total, onPrev, onNext, onRate, flipped }) {
  return (
    <div className="mt-5 flex items-center justify-between gap-3">
      <button onClick={onPrev} disabled={idx === 0} className="pm-btn-secondary inline-flex items-center gap-1 disabled:opacity-40" data-testid="term-deck-prev">
        <ArrowLeft size={14} /> Prev
      </button>
      {flipped ? (
        <div className="flex gap-2">
          <button onClick={() => onRate(false)} className="rounded-2xl px-4 py-2.5 inline-flex items-center gap-1.5 text-white font-medium transition-transform hover:-translate-y-0.5" style={{ background: "var(--weakness)" }} data-testid="term-deck-rate-again">
            <X size={14} /> Review again
          </button>
          <button onClick={() => onRate(true)} className="rounded-2xl px-4 py-2.5 inline-flex items-center gap-1.5 text-white font-medium transition-transform hover:-translate-y-0.5" style={{ background: "var(--mastery)" }} data-testid="term-deck-rate-known">
            <Check size={14} /> Got it
          </button>
        </div>
      ) : <span className="text-xs" style={{ color: "var(--text-muted)" }}>Flip to rate</span>}
      <button onClick={onNext} disabled={idx >= total - 1} className="pm-btn-secondary inline-flex items-center gap-1 disabled:opacity-40" data-testid="term-deck-next">
        Next <ArrowRight size={14} />
      </button>
    </div>
  );
}

function RegenerateDialog({ open, onOpenChange, cost, onConfirm }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="term-deck-regen-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Replace this set with a fresh one?</AlertDialogTitle>
          <AlertDialogDescription>
            This costs {costLabel(cost)}. Your current cards, ratings and images will be replaced by the new set.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="term-deck-regen-cancel">Keep current set</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid="term-deck-regen-confirm">Regenerate</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function useTermDeck(topicId) {
  const [state, setState] = useState({ loading: true, deck: null, setCost: 0.5, imageCost: 0.5 });
  const poll = useRef(null);
  const load = useCallback(async () => {
    const r = await api.get(`/term-decks/${topicId}`);
    setState({ loading: false, deck: r.data.deck, setCost: r.data.set_cost, imageCost: r.data.image_cost });
    return r.data.deck;
  }, [topicId]);
  useEffect(() => { load().catch(() => setState((s) => ({ ...s, loading: false }))); }, [load]);
  const generating = state.deck?.status === "generating";
  useEffect(() => {
    if (!generating) return undefined;
    poll.current = setInterval(async () => {
      const d = await load().catch(() => null);
      if (d && d.status !== "generating") refreshBilling();
    }, 4000);
    return () => clearInterval(poll.current);
  }, [generating, load]);
  return { ...state, setDeck: (deck) => setState((s) => ({ ...s, deck })), generating };
}

export default function TermDeck({ topic, onBack, onOpenAnalogy }) {
  const { loading, deck, setDeck, setCost, imageCost, generating } = useTermDeck(topic.id);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [busy, setBusy] = useState(false);
  const [genError, setGenError] = useState(null);
  const [imgBusy, setImgBusy] = useState(null);
  const [imgError, setImgError] = useState(null);
  const [regenOpen, setRegenOpen] = useState(false);

  const cards = deck?.cards || [];
  const card = cards[Math.min(idx, cards.length - 1)];
  const knownCount = cards.filter((c) => c.known === true).length;

  const generate = async (regenerate = false) => {
    setBusy(true); setGenError(null);
    try {
      const r = await api.post(`/term-decks/${topic.id}/generate`, { regenerate }, { silent: true });
      setDeck(r.data.deck);
      setIdx(0); setFlipped(false);
      refreshBilling();
    } catch (e) {
      setGenError(readError(e, "Couldn't start generating. Try again in a moment."));
    } finally { setBusy(false); }
  };

  const go = (n) => { setIdx(n); setFlipped(false); setImgError(null); };

  const rate = async (known) => {
    const updated = cards.map((c) => (c.id === card.id ? { ...c, known } : c));
    setDeck({ ...deck, cards: updated });
    api.post(`/term-decks/${topic.id}/cards/${card.id}/rate`, { known }).catch(() => {});
    if (idx < cards.length - 1) go(idx + 1);
  };

  const addImage = async (style) => {
    const cid = card.id;
    setImgBusy(cid); setImgError(null);
    try {
      const r = await api.post(`/term-decks/${topic.id}/cards/${cid}/image`, { style }, { silent: true });
      setDeck({ ...deck, cards: cards.map((c) => (c.id === cid ? { ...c, image_url: r.data.image_url, image_style: r.data.image_style } : c)) });
      refreshBilling();
    } catch (e) {
      setImgError(readError(e, "Image generation failed. Try again?"));
    } finally { setImgBusy(null); }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="term-deck-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <button onClick={onBack} className="text-xs inline-flex items-center gap-1 mb-1 hover:underline" style={{ color: "var(--primary)" }} data-testid="term-deck-back">
            <ArrowLeft size={12} /> All topics
          </button>
          <h1 className="font-display text-2xl md:text-3xl font-bold" data-testid="term-deck-title">{topic.name}</h1>
          {deck?.aamc_code && <div className="text-xs mt-1 font-mono" style={{ color: "var(--text-muted)" }}>AAMC {deck.aamc_code}</div>}
        </div>
        <button onClick={onOpenAnalogy} className="text-xs shrink-0 pm-btn-secondary inline-flex items-center gap-1.5" data-testid="term-deck-open-analogy">
          <Sparkles size={12} /> Analogy card
        </button>
      </div>

      {loading ? (
        <div className="py-16 text-center" data-testid="term-deck-loading"><Loader2 size={28} className="animate-spin mx-auto" style={{ color: "var(--primary)" }} /></div>
      ) : generating ? (
        <GeneratingPanel topic={topic} />
      ) : cards.length === 0 ? (
        <GeneratePanel topic={topic} cost={setCost} busy={busy} error={genError}
          failedMsg={deck?.status === "failed" ? deck.error : null} onGenerate={() => generate(false)} />
      ) : (
        <>
          <div className="flex items-center justify-between mb-3 text-sm flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5" style={{ color: "var(--mastery)" }} data-testid="term-deck-saved-badge">
              <BookmarkCheck size={14} /> Saved · reopens free
            </span>
            <span style={{ color: "var(--text-muted)" }} data-testid="term-deck-progress">
              Card {idx + 1} of {cards.length} · {knownCount} known
            </span>
          </div>
          {deck?.status === "failed" && <p className="text-sm mb-3" style={{ color: "var(--weakness)" }} data-testid="term-deck-failed">{deck.error}</p>}
          <StudyCard card={card} flipped={flipped} onFlip={() => setFlipped(!flipped)}
            imageProps={{ cost: imageCost, busy: imgBusy === card.id, error: imgError, onAdd: addImage }} />
          <StudyControls idx={idx} total={cards.length} flipped={flipped}
            onPrev={() => go(idx - 1)} onNext={() => go(idx + 1)} onRate={rate} />
          <div className="mt-8 pt-5 border-t flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: "var(--border)" }}>
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>Want a different take on this topic?</span>
            <button onClick={() => setRegenOpen(true)} disabled={busy} className="text-xs inline-flex items-center gap-1.5 hover:underline disabled:opacity-60" style={{ color: "var(--primary)" }} data-testid="term-deck-regenerate-btn">
              <RefreshCw size={12} /> Regenerate set · {costLabel(setCost)}
            </button>
          </div>
          <ErrorNote error={genError} testid="term-deck-regen-error" />
          <RegenerateDialog open={regenOpen} onOpenChange={setRegenOpen} cost={setCost} onConfirm={() => generate(true)} />
        </>
      )}
    </div>
  );
}
